import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaFileExcel } from 'react-icons/fa';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import './ReportesGenerales.css';
import Header from '../common/Header';
import { getApiUrl } from '../../utils/api';

const ITEMS_PER_PAGE = 50;

// Colores Power BI
const COLORS_TIPO = {
  mesa: '#2563eb', llevar: '#059669', domicilio: '#dc2626',
  uber: '#ea580c', rappi: '#7c3aed', delibot: '#0891b2'
};
const COLORS_PAGO = ['#43A047', '#1E88E5', '#F57C00', '#8E24AA', '#E91E63'];
const COLORS_CHART = ['#2563eb', '#059669', '#dc2626', '#ea580c', '#7c3aed', '#0891b2', '#d97706', '#be123c', '#0369a1', '#166534'];

const CustomTooltip = ({ active, payload, label, isMoney = true }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rg-chart-tooltip">
      <p className="rg-chart-tooltip__label">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color || entry.fill }}>
          {entry.name}: {isMoney ? `$${Number(entry.value).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : Number(entry.value).toLocaleString('es-MX')}
        </p>
      ))}
    </div>
  );
};

const ReportesGenerales = () => {
  const [tabActual, setTabActual] = useState('total-ventas');
  const [resumenVentas, setResumenVentas] = useState(null);
  const [pedidosReporte, setPedidosReporte] = useState([]);
  const [detallePlatillos, setDetallePlatillos] = useState([]);
  const [detalleProductos, setDetalleProductos] = useState([]);
  const [productosCancelados, setProductosCancelados] = useState([]);
  const [ventasPorMesero, setVentasPorMesero] = useState([]);
  const [reporteUtilidad, setReporteUtilidad] = useState({ platillos: [], resumen: null });
  const [ajustesInventario, setAjustesInventario] = useState([]);
  const [meseroFiltro, setMeseroFiltro] = useState('');
  const [corteSeleccionado, setCorteSeleccionado] = useState('hoy');
  const [fechaCorte, setFechaCorte] = useState(() => new Date().toISOString().split('T')[0]);
  const [cortesDelDia, setCortesDelDia] = useState([]);
  const [modoFiltro, setModoFiltro] = useState('cortes');
  const [fechaInicio, setFechaInicio] = useState(() => new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(() => new Date().toISOString().split('T')[0]);
  const [tiposCuenta, setTiposCuenta] = useState({
    mesa: true, llevar: true, domicilio: true, uber: true, rappi: true, delibot: true
  });
  const [tiposPago, setTiposPago] = useState({
    efectivo: true, tarjeta: true, transferencia: true, cupon: true, cortesia: true
  });
  const [cargando, setCargando] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);

  useEffect(() => {
    if (modoFiltro !== 'cortes') return;
    const resolverCortes = async () => {
      try {
        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getCortes', fecha: fechaCorte })
        });
        const result = await response.json();
        const cortes = (result.success && Array.isArray(result.data)) ? result.data : [];
        setCortesDelDia(cortes);
        setCorteSeleccionado(cortes.length > 0 ? cortes[0].idCorte : '');
      } catch (error) {
        console.error('Error resolviendo cortes:', error);
        setCortesDelDia([]);
        setCorteSeleccionado('');
      }
    };
    resolverCortes();
  }, [modoFiltro, fechaCorte]);

  const buildParams = useCallback(() => {
    const tiposCuentaActivos = Object.entries(tiposCuenta)
      .filter(([, v]) => v).map(([k]) => k);
    const tiposPagoActivos = Object.entries(tiposPago)
      .filter(([, v]) => v).map(([k]) => k);

    const base = {
      modo: modoFiltro === 'cortes' ? 'corte' : 'fechas',
      tiposCuenta: tiposCuentaActivos,
      tiposPago: tiposPagoActivos
    };

    if (modoFiltro === 'cortes') {
      base.idCorte = corteSeleccionado;
    } else {
      base.fechaInicio = fechaInicio;
      base.fechaFin = fechaFin;
    }
    return base;
  }, [modoFiltro, corteSeleccionado, fechaInicio, fechaFin, tiposCuenta, tiposPago]);

  const buscarReportes = useCallback(async () => {
    setCargando(true);
    setPaginaActual(1);
    const params = buildParams();

    try {
      const [ventasRes, platillosRes, productosRes, canceladosRes, meseroRes, utilidadRes, ajustesRes] = await Promise.all([
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteVentas', ...params })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteDetallePlatillos', ...params })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteDetalleProductos', ...params })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteProductosCancelados', ...params })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteVentasPorMesero', ...params, meseroFiltro })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteUtilidad', ...params })
        }).then(r => r.json()),
        fetch(getApiUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tipo: 'getReporteAjustesInventario', ...params })
        }).then(r => r.json())
      ]);

      if (ventasRes.success && ventasRes.data) {
        setResumenVentas(ventasRes.data.resumen);
        setPedidosReporte(ventasRes.data.pedidos || []);
      } else {
        setResumenVentas(null);
        setPedidosReporte([]);
      }
      setDetallePlatillos(platillosRes.success ? (platillosRes.data || []) : []);
      setDetalleProductos(productosRes.success ? (productosRes.data || []) : []);
      setProductosCancelados(canceladosRes.success ? (canceladosRes.data || []) : []);
      setVentasPorMesero(meseroRes.success ? (meseroRes.data || []) : []);
      setReporteUtilidad(utilidadRes.success && utilidadRes.data ? { platillos: utilidadRes.data.platillos || [], resumen: utilidadRes.data.resumen || null } : { platillos: [], resumen: null });
      setAjustesInventario(ajustesRes.success ? (ajustesRes.data || []) : []);
    } catch (error) {
      console.error('Error buscando reportes:', error);
    } finally {
      setCargando(false);
    }
  }, [buildParams, meseroFiltro]);

  useEffect(() => {
    buscarReportes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDataActual = () => {
    switch (tabActual) {
      case 'ventas': return pedidosReporte;
      case 'detalle-platillos': return detallePlatillos;
      case 'detalle-productos': return detalleProductos;
      case 'cancelados': return productosCancelados;
      case 'meseros': return ventasPorMesero;
      case 'utilidad': return reporteUtilidad.platillos;
      case 'ajustes-inventario': return ajustesInventario;
      default: return [];
    }
  };

  const dataPaginada = () => {
    const data = getDataActual();
    const inicio = (paginaActual - 1) * ITEMS_PER_PAGE;
    return data.slice(inicio, inicio + ITEMS_PER_PAGE);
  };

  const totalPaginas = Math.max(1, Math.ceil(getDataActual().length / ITEMS_PER_PAGE));

  const handleTabChange = (tab) => {
    setTabActual(tab);
    setPaginaActual(1);
  };

  const handleTipoCuentaChange = (key) => {
    setTiposCuenta(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTipoPagoChange = (key) => {
    setTiposPago(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatMoney = (val) => {
    return '$' + Number(val || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatNumber = (val, decimals = 0) => {
    return Number(val || 0).toLocaleString('es-MX', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const getBadgeClass = (tipo) => {
    const t = (tipo || '').toLowerCase();
    const map = { mesa: 'badge-mesa', llevar: 'badge-llevar', domicilio: 'badge-domicilio', uber: 'badge-uber', rappi: 'badge-rappi', delibot: 'badge-delibot' };
    return `badge-tipo ${map[t] || ''}`;
  };

  const exportarCSV = () => {
    let headers = [];
    let rows = [];

    if (tabActual === 'total-ventas' && resumenVentas) {
      headers = ['Concepto', 'Valor'];
      rows.push(['Total Ventas', resumenVentas.totalVentas]);
      rows.push(['Total Cuentas', resumenVentas.totalCuentas]);
      rows.push(['Ticket Promedio', resumenVentas.ticketPromedio]);
      rows.push(['Efectivo', resumenVentas.formasPago.efectivo]);
      rows.push(['Tarjeta', resumenVentas.formasPago.tarjeta]);
      rows.push(['Transferencia', resumenVentas.formasPago.transferencia]);
      rows.push(['Cupon', resumenVentas.formasPago.cupon]);
      rows.push(['Cortesia', resumenVentas.formasPago.cortesia]);
      rows.push(['Total Descuentos', resumenVentas.totalDescuentos]);
      rows.push(['Total Gastos', resumenVentas.totalGastos]);
      rows.push(['Venta Neta', resumenVentas.ventaNeta]);
      if (resumenVentas.ventasPorTipo) {
        resumenVentas.ventasPorTipo.forEach(vt => {
          rows.push([`Ventas ${vt.tipo}`, vt.monto]);
        });
      }
    } else if (tabActual === 'ventas') {
      headers = ['#', 'Fecha', 'Hora', 'Tipo', 'Mesero', 'Cliente', 'Productos', 'Subtotal', 'Descuento', 'Total', 'Forma Pago', 'Status'];
      rows = pedidosReporte.map(p => [p.idPedido, p.fecha, p.hora, p.tipo, p.mesero, p.cliente, p.cantidadProductos, p.subtotal, p.descuento, p.total, p.formaPago, p.status]);
    } else if (tabActual === 'detalle-platillos') {
      headers = ['Platillo', 'Categoria', 'Cant. Vendida', 'Precio Unit.', 'Total Ventas'];
      rows = detallePlatillos.map(p => [p.nombre, p.categoria, p.cantidadVendida, p.precioUnitario, p.totalVentas]);
    } else if (tabActual === 'detalle-productos') {
      headers = ['Ingrediente', 'Unidad', 'Cant. Consumida', 'Costo Unit.', 'Costo Total'];
      rows = detalleProductos.map(p => [p.nombre, p.unidad, p.cantidadConsumida, p.costoUnitario, p.costoTotal]);
    } else if (tabActual === 'cancelados') {
      headers = ['Pedido', 'Fecha', 'Hora', 'Tipo', 'Mesero', 'Platillo', 'Categoria', 'Cant.', 'Precio', 'Importe', 'Autorizado Por', 'Motivo'];
      rows = productosCancelados.map(p => [p.numeroPedido, p.fecha, p.hora, p.tipo, p.mesero, p.nombrePlatillo, p.categoria, p.cantidad, p.precio, p.importePerdido, p.autorizadoPor, p.motivo]);
    } else if (tabActual === 'meseros') {
      headers = ['Mesero', 'Cuentas', 'Total Ventas', 'Ticket Prom.', 'Efectivo', 'Tarjeta', 'Transferencia', 'Cupon', 'Cortesia', 'Descuentos'];
      rows = ventasPorMesero.map(m => [m.mesero, m.totalCuentas, m.totalVentas, m.ticketPromedio, m.efectivo, m.tarjeta, m.transferencia, m.cupon, m.cortesia, m.totalDescuentos]);
    } else if (tabActual === 'utilidad') {
      headers = ['Platillo', 'Categoria', 'Cant.', 'Ventas', 'Costo', 'Utilidad', '% Utilidad'];
      rows = reporteUtilidad.platillos.map(p => [p.nombre, p.categoria, p.cantidadVendida, p.totalVentas, p.totalCosto, p.utilidad, p.porcentajeUtilidad + '%']);
    } else if (tabActual === 'ajustes-inventario') {
      headers = ['Fecha', 'Hora', 'Ingrediente', 'Unidad', 'Existencia Anterior', 'Nueva Existencia', 'Diferencia', 'Usuario'];
      rows = ajustesInventario.map(a => [a.fecha, a.hora, a.ingrediente, a.unidad, a.existenciaAnterior, a.nuevaExistencia, a.diferencia, a.usuario]);
    }

    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_${tabActual}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ===== RENDER TABS =====

  const renderTotalVentas = () => {
    if (!resumenVentas) return <div className="rg-empty">Presiona "Buscar" para generar el reporte</div>;
    const r = resumenVentas;

    // Data para donut de ventas por tipo
    const dataTipos = (r.ventasPorTipo || []).map(vt => ({
      name: (vt.tipo || '').toUpperCase(),
      value: Number(vt.monto) || 0,
      cantidad: vt.cantidad,
      fill: COLORS_TIPO[(vt.tipo || '').toLowerCase()] || '#999'
    })).filter(d => d.value > 0);

    // Data para donut de formas de pago
    const pagoEntries = [
      ['Efectivo', Number(r.formasPago?.efectivo) || 0],
      ['Tarjeta', Number(r.formasPago?.tarjeta) || 0],
      ['Transferencia', Number(r.formasPago?.transferencia) || 0],
      ['Cupon', Number(r.formasPago?.cupon) || 0],
      ['Cortesia', Number(r.formasPago?.cortesia) || 0]
    ];
    const dataPagos = pagoEntries
      .map(([name, value], i) => ({ name, value, fill: COLORS_PAGO[i] }))
      .filter(d => d.value > 0);

    // Data para barras de cuentas por tipo
    const dataBarTipos = (r.ventasPorTipo || []).map(vt => ({
      name: (vt.tipo || '').toUpperCase(),
      cuentas: Number(vt.cantidad) || 0,
      monto: Number(vt.monto) || 0,
      fill: COLORS_TIPO[(vt.tipo || '').toLowerCase()] || '#999'
    }));

    const renderDonutLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      if (percent < 0.05) return null;
      const RADIAN = Math.PI / 180;
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);
      return (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <div className="rg-resumen">
        {/* KPI Cards */}
        <div className="rg-cards">
          <div className="rg-card rg-card--ventas">
            <div className="rg-card__label">Total Ventas</div>
            <div className="rg-card__valor">{formatMoney(r.totalVentas)}</div>
          </div>
          <div className="rg-card rg-card--cuentas">
            <div className="rg-card__label">Total Cuentas</div>
            <div className="rg-card__valor">{r.totalCuentas}</div>
          </div>
          <div className="rg-card rg-card--ticket">
            <div className="rg-card__label">Ticket Promedio</div>
            <div className="rg-card__valor">{formatMoney(r.ticketPromedio)}</div>
          </div>
          <div className="rg-card rg-card--neta">
            <div className="rg-card__label">Venta Neta</div>
            <div className="rg-card__valor">{formatMoney(r.ventaNeta)}</div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="rg-charts-row">
          {/* Donut: Ventas por Tipo */}
          <div className="rg-chart-panel">
            <h4 className="rg-chart-title">Ventas por Tipo de Cuenta</h4>
            {dataTipos.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={dataTipos}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                    labelLine={false}
                    label={renderDonutLabel}
                  >
                    {dataTipos.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} stroke="white" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isMoney />} />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value) => <span style={{ fontSize: 11, color: '#475569' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="rg-empty-mini">Sin datos</div>
            )}
          </div>

          {/* Donut: Formas de Pago */}
          <div className="rg-chart-panel">
            <h4 className="rg-chart-title">Formas de Pago</h4>
            {dataPagos.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={dataPagos}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                    labelLine={false}
                    label={renderDonutLabel}
                  >
                    {dataPagos.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} stroke="white" strokeWidth={2} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isMoney />} />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value) => <span style={{ fontSize: 11, color: '#475569' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="rg-empty-mini">Sin datos</div>
            )}
          </div>

          {/* Bar: Monto por Tipo */}
          <div className="rg-chart-panel">
            <h4 className="rg-chart-title">Monto y Cuentas por Tipo</h4>
            {dataBarTipos.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={dataBarTipos} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip isMoney />} />
                  <Bar dataKey="monto" name="Monto" radius={[4, 4, 0, 0]}>
                    {dataBarTipos.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="rg-empty-mini">Sin datos</div>
            )}
          </div>
        </div>

        {/* Desglose detallado */}
        <div className="rg-desglose">
          <div className="rg-desglose__panel">
            <h4>Ventas por Tipo</h4>
            {r.ventasPorTipo && r.ventasPorTipo.length > 0 ? (
              r.ventasPorTipo.map((vt, i) => (
                <div className="rg-desglose__row" key={i}>
                  <span className={getBadgeClass(vt.tipo)}>{vt.tipo}</span>
                  <span className="rg-desglose__cant">{vt.cantidad} cuentas</span>
                  <span className="rg-desglose__monto">{formatMoney(vt.monto)}</span>
                </div>
              ))
            ) : (
              <div className="rg-empty-mini">Sin datos</div>
            )}
          </div>

          <div className="rg-desglose__panel">
            <h4>Formas de Pago</h4>
            {pagoEntries.map(([label, val]) => (
              <div className="rg-desglose__row" key={label}>
                <span className="rg-desglose__label">{label}</span>
                <span className="rg-desglose__monto">{formatMoney(val)}</span>
              </div>
            ))}
          </div>

          <div className="rg-desglose__panel">
            <h4>Descuentos y Gastos</h4>
            <div className="rg-desglose__row">
              <span className="rg-desglose__label">Descuentos</span>
              <span className="rg-desglose__monto rg-desglose__monto--neg">-{formatMoney(r.totalDescuentos)}</span>
            </div>
            <div className="rg-desglose__row">
              <span className="rg-desglose__label">Gastos</span>
              <span className="rg-desglose__monto rg-desglose__monto--neg">-{formatMoney(r.totalGastos)}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderVentas = () => {
    const items = dataPaginada();
    if (items.length === 0) return <div className="rg-empty">No hay pedidos para mostrar</div>;
    const totalPagina = items.reduce((s, p) => s + p.total, 0);

    return (
      <div className="rg-tabla-wrap">
        <table className="rg-tabla">
          <thead>
            <tr>
              <th>#</th><th>Fecha</th><th>Hora</th><th>Tipo</th><th>Mesero</th>
              <th>Cliente</th><th className="rg-num">Prod.</th><th className="rg-num">Subtotal</th><th className="rg-num">Desc.</th><th className="rg-num">Total</th><th>Forma Pago</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.idPedido}>
                <td>{p.idPedido}</td>
                <td>{p.fecha}</td>
                <td>{p.hora}</td>
                <td><span className={getBadgeClass(p.tipo)}>{p.tipo}</span></td>
                <td>{p.mesero}</td>
                <td>{p.cliente}</td>
                <td className="rg-num">{p.cantidadProductos}</td>
                <td className="rg-num">{formatMoney(p.subtotal)}</td>
                <td className="rg-num">{formatMoney(p.descuento)}</td>
                <td className="rg-num">{formatMoney(p.total)}</td>
                <td>{p.formaPago}</td>
                <td>{p.status}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td></td><td></td><td></td><td></td><td></td>
              <td></td><td></td><td></td>
              <td className="rg-total-label">Total pagina:</td>
              <td className="rg-num rg-total-val">{formatMoney(totalPagina)}</td>
              <td></td><td></td>
            </tr>
            {pedidosReporte.length > ITEMS_PER_PAGE && (
              <tr>
                <td></td><td></td><td></td><td></td><td></td>
                <td></td><td></td><td></td>
                <td className="rg-total-label">Total general:</td>
                <td className="rg-num rg-total-val">{formatMoney(pedidosReporte.reduce((s, p) => s + p.total, 0))}</td>
                <td></td><td></td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    );
  };

  const renderDetallePlatillos = () => {
    const items = dataPaginada();
    if (items.length === 0) return <div className="rg-empty">No hay datos de platillos</div>;
    const totalPagina = items.reduce((s, p) => s + p.totalVentas, 0);

    // Top 10 platillos chart data
    const top10 = [...detallePlatillos]
      .sort((a, b) => b.totalVentas - a.totalVentas)
      .slice(0, 10)
      .map(p => ({
        name: (p.nombre || '').length > 18 ? (p.nombre || '').substring(0, 18) + '...' : (p.nombre || ''),
        ventas: Number(p.totalVentas) || 0,
        cantidad: Number(p.cantidadVendida) || 0
      }));

    return (
      <div>
        {/* Chart Top 10 */}
        {top10.length > 0 && paginaActual === 1 && (
          <div className="rg-chart-full">
            <h4 className="rg-chart-title">Top 10 Platillos por Venta</h4>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={top10} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip isMoney />} />
                <Bar dataKey="ventas" name="Ventas" radius={[0, 4, 4, 0]}>
                  {top10.map((_, i) => (
                    <Cell key={i} fill={COLORS_CHART[i % COLORS_CHART.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      <div className="rg-tabla-wrap">
        <table className="rg-tabla">
          <thead>
            <tr><th>Platillo</th><th>Categoria</th><th className="rg-num">Cant. Vendida</th><th className="rg-num">Precio Unit.</th><th className="rg-num">Total Ventas</th></tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={`${p.idPlatillo}-${i}`}>
                <td>{p.nombre}</td>
                <td>{p.categoria}</td>
                <td className="rg-num">{formatNumber(p.cantidadVendida)}</td>
                <td className="rg-num">{formatMoney(p.precioUnitario)}</td>
                <td className="rg-num">{formatMoney(p.totalVentas)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="rg-total-label">Total pagina:</td>
              <td></td><td></td><td></td>
              <td className="rg-num rg-total-val">{formatMoney(totalPagina)}</td>
            </tr>
            {detallePlatillos.length > ITEMS_PER_PAGE && (
              <tr>
                <td className="rg-total-label">Total general:</td>
                <td></td><td></td><td></td>
                <td className="rg-num rg-total-val">{formatMoney(detallePlatillos.reduce((s, p) => s + p.totalVentas, 0))}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      </div>
    );
  };

  const renderDetalleProductos = () => {
    const items = dataPaginada();
    if (items.length === 0) return <div className="rg-empty">No hay datos de productos</div>;
    const totalPagina = items.reduce((s, p) => s + p.costoTotal, 0);

    // Top 10 ingredientes por costo
    const top10Ing = [...detalleProductos]
      .sort((a, b) => b.costoTotal - a.costoTotal)
      .slice(0, 10)
      .map(p => ({
        name: (p.nombre || '').length > 20 ? (p.nombre || '').substring(0, 20) + '...' : (p.nombre || ''),
        costo: Number(p.costoTotal) || 0
      }));

    return (
      <div>
        {top10Ing.length > 0 && paginaActual === 1 && (
          <div className="rg-chart-full">
            <h4 className="rg-chart-title">Top 10 Insumos por Costo Consumido</h4>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={top10Ing} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip isMoney />} />
                <Bar dataKey="costo" name="Costo Total" radius={[0, 4, 4, 0]}>
                  {top10Ing.map((_, i) => (
                    <Cell key={i} fill={COLORS_CHART[i % COLORS_CHART.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      <div className="rg-tabla-wrap">
        <table className="rg-tabla">
          <thead>
            <tr><th>Ingrediente</th><th>Unidad</th><th className="rg-num">Cant. Consumida</th><th className="rg-num">Costo Unit.</th><th className="rg-num">Costo Total</th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.idIngrediente}>
                <td>{p.nombre}</td>
                <td>{p.unidad}</td>
                <td className="rg-num">{formatNumber(p.cantidadConsumida, 3)}</td>
                <td className="rg-num">{formatMoney(p.costoUnitario)}</td>
                <td className="rg-num">{formatMoney(p.costoTotal)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="rg-total-label">Total pagina:</td>
              <td></td><td></td><td></td>
              <td className="rg-num rg-total-val">{formatMoney(totalPagina)}</td>
            </tr>
            {detalleProductos.length > ITEMS_PER_PAGE && (
              <tr>
                <td className="rg-total-label">Total general:</td>
                <td></td><td></td><td></td>
                <td className="rg-num rg-total-val">{formatMoney(detalleProductos.reduce((s, p) => s + p.costoTotal, 0))}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      </div>
    );
  };

  const renderCancelados = () => {
    const items = dataPaginada();
    if (items.length === 0) return <div className="rg-empty">No hay productos cancelados</div>;
    const totalPagina = items.reduce((s, p) => s + p.importePerdido, 0);

    return (
      <div className="rg-tabla-wrap">
        <table className="rg-tabla">
          <thead>
            <tr>
              <th>Pedido</th><th>Fecha</th><th>Hora</th><th>Tipo</th><th>Mesero</th>
              <th>Platillo</th><th className="rg-num">Cant.</th><th className="rg-num">Importe</th><th>Autorizado Por</th><th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, i) => (
              <tr key={`${p.idPedido}-${i}`} className="rg-row-cancel">
                <td>{p.numeroPedido}</td>
                <td>{p.fecha}</td>
                <td>{p.hora}</td>
                <td><span className={getBadgeClass(p.tipo)}>{p.tipo}</span></td>
                <td>{p.mesero}</td>
                <td><strong>{p.nombrePlatillo}</strong></td>
                <td className="rg-num">{formatNumber(p.cantidad)}</td>
                <td className="rg-num rg-cancel-importe">{formatMoney(p.importePerdido)}</td>
                <td>{p.autorizadoPor}</td>
                <td className="rg-motivo">{p.motivo}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td></td><td></td><td></td><td></td><td></td>
              <td></td>
              <td className="rg-total-label">Total perdido:</td>
              <td className="rg-num rg-total-val rg-total-val--neg">{formatMoney(totalPagina)}</td>
              <td></td><td></td>
            </tr>
            {productosCancelados.length > ITEMS_PER_PAGE && (
              <tr>
                <td></td><td></td><td></td><td></td><td></td>
                <td></td>
                <td className="rg-total-label">Total general:</td>
                <td className="rg-num rg-total-val rg-total-val--neg">{formatMoney(productosCancelados.reduce((s, p) => s + p.importePerdido, 0))}</td>
                <td></td><td></td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    );
  };

  const renderVentasPorMesero = () => {
    const filteredData = meseroFiltro
      ? ventasPorMesero.filter(m => (m.mesero || '').toLowerCase().includes(meseroFiltro.toLowerCase()))
      : ventasPorMesero;

    // Chart: Top meseros por ventas
    const topMeseros = [...filteredData]
      .sort((a, b) => b.totalVentas - a.totalVentas)
      .slice(0, 10)
      .map(m => ({
        name: (m.mesero || 'Sin mesero').length > 16 ? (m.mesero || 'Sin mesero').substring(0, 16) + '...' : (m.mesero || 'Sin mesero'),
        ventas: Number(m.totalVentas) || 0,
        cuentas: Number(m.totalCuentas) || 0
      }));

    const totalGeneral = filteredData.reduce((s, m) => s + (Number(m.totalVentas) || 0), 0);
    const totalCuentas = filteredData.reduce((s, m) => s + (Number(m.totalCuentas) || 0), 0);

    return (
      <div>
        {/* Filtro de mesero */}
        <div className="rg-mesero-filtro">
          <FaSearch className="rg-mesero-filtro__icon" />
          <input
            type="text"
            placeholder="Filtrar por nombre de mesero..."
            value={meseroFiltro}
            onChange={(e) => setMeseroFiltro(e.target.value)}
            className="rg-mesero-filtro__input"
          />
          {meseroFiltro && (
            <button className="rg-mesero-filtro__clear" onClick={() => setMeseroFiltro('')}>x</button>
          )}
          <span className="rg-mesero-filtro__count">{filteredData.length} mesero(s)</span>
        </div>

        {/* KPI Cards meseros */}
        <div className="rg-cards rg-cards--mesero">
          <div className="rg-card rg-card--ventas">
            <div className="rg-card__label">Total Ventas</div>
            <div className="rg-card__valor">{formatMoney(totalGeneral)}</div>
          </div>
          <div className="rg-card rg-card--cuentas">
            <div className="rg-card__label">Total Cuentas</div>
            <div className="rg-card__valor">{totalCuentas}</div>
          </div>
          <div className="rg-card rg-card--ticket">
            <div className="rg-card__label">Meseros</div>
            <div className="rg-card__valor">{filteredData.length}</div>
          </div>
          <div className="rg-card rg-card--neta">
            <div className="rg-card__label">Ticket Promedio</div>
            <div className="rg-card__valor">{formatMoney(totalCuentas > 0 ? totalGeneral / totalCuentas : 0)}</div>
          </div>
        </div>

        {/* Chart */}
        {topMeseros.length > 0 && (
          <div className="rg-chart-full">
            <h4 className="rg-chart-title">Top Meseros por Venta</h4>
            <ResponsiveContainer width="100%" height={Math.max(200, topMeseros.length * 40)}>
              <BarChart data={topMeseros} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip isMoney />} />
                <Bar dataKey="ventas" name="Ventas" radius={[0, 4, 4, 0]}>
                  {topMeseros.map((_, i) => (
                    <Cell key={i} fill={COLORS_CHART[i % COLORS_CHART.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tabla */}
        {filteredData.length === 0 ? (
          <div className="rg-empty">No hay datos de meseros</div>
        ) : (
          <div className="rg-tabla-wrap">
            <table className="rg-tabla">
              <thead>
                <tr>
                  <th>Mesero</th>
                  <th className="rg-num">Cuentas</th>
                  <th className="rg-num">Total Ventas</th>
                  <th className="rg-num">Ticket Prom.</th>
                  <th className="rg-num">Efectivo</th>
                  <th className="rg-num">Tarjeta</th>
                  <th className="rg-num">Transferencia</th>
                  <th className="rg-num">Cupon</th>
                  <th className="rg-num">Cortesia</th>
                  <th className="rg-num">Descuentos</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((m, i) => (
                  <tr key={i}>
                    <td><strong>{m.mesero || 'Sin mesero'}</strong></td>
                    <td className="rg-num">{m.totalCuentas}</td>
                    <td className="rg-num">{formatMoney(m.totalVentas)}</td>
                    <td className="rg-num">{formatMoney(m.ticketPromedio)}</td>
                    <td className="rg-num">{formatMoney(m.efectivo)}</td>
                    <td className="rg-num">{formatMoney(m.tarjeta)}</td>
                    <td className="rg-num">{formatMoney(m.transferencia)}</td>
                    <td className="rg-num">{formatMoney(m.cupon)}</td>
                    <td className="rg-num">{formatMoney(m.cortesia)}</td>
                    <td className="rg-num">{formatMoney(m.totalDescuentos)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="rg-total-label">TOTALES</td>
                  <td className="rg-total-val">{totalCuentas}</td>
                  <td className="rg-total-val">{formatMoney(totalGeneral)}</td>
                  <td className="rg-total-val">{formatMoney(totalCuentas > 0 ? totalGeneral / totalCuentas : 0)}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.efectivo) || 0), 0))}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.tarjeta) || 0), 0))}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.transferencia) || 0), 0))}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.cupon) || 0), 0))}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.cortesia) || 0), 0))}</td>
                  <td className="rg-total-val">{formatMoney(filteredData.reduce((s, m) => s + (Number(m.totalDescuentos) || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderUtilidad = () => {
    const { platillos, resumen } = reporteUtilidad;
    if (!resumen) return <div className="rg-empty">Presiona "Buscar" para generar el reporte</div>;

    const items = dataPaginada();
    const totalCantPag = items.reduce((s, p) => s + p.cantidadVendida, 0);
    const totalVentasPag = items.reduce((s, p) => s + p.totalVentas, 0);
    const totalCostoPag = items.reduce((s, p) => s + p.totalCosto, 0);
    const utilidadPag = totalVentasPag - totalCostoPag;

    // Top 10 platillos por utilidad para chart
    const top10 = [...platillos]
      .sort((a, b) => b.utilidad - a.utilidad)
      .slice(0, 10)
      .map(p => ({
        name: (p.nombre || '').length > 18 ? (p.nombre || '').substring(0, 18) + '...' : (p.nombre || ''),
        utilidad: p.utilidad,
        ventas: p.totalVentas,
        costo: p.totalCosto
      }));

    // Color según % utilidad
    const getUtilidadColor = (pct) => {
      if (pct >= 60) return '#16a34a';
      if (pct >= 40) return '#65a30d';
      if (pct >= 20) return '#d97706';
      if (pct > 0) return '#ea580c';
      return '#dc2626';
    };

    return (
      <div>
        {/* KPI Cards */}
        <div className="rg-cards">
          <div className="rg-card rg-card--ventas">
            <div className="rg-card__label">Total Ventas</div>
            <div className="rg-card__valor">{formatMoney(resumen.totalVentas)}</div>
          </div>
          <div className="rg-card" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="rg-card__label">Total Costo</div>
            <div className="rg-card__valor">{formatMoney(resumen.totalCosto)}</div>
          </div>
          <div className="rg-card rg-card--neta">
            <div className="rg-card__label">Utilidad</div>
            <div className="rg-card__valor">{formatMoney(resumen.utilidadGeneral)}</div>
          </div>
          <div className="rg-card" style={{ borderLeft: `4px solid ${getUtilidadColor(resumen.porcentajeGeneral)}` }}>
            <div className="rg-card__label">% Utilidad General</div>
            <div className="rg-card__valor" style={{ color: getUtilidadColor(resumen.porcentajeGeneral) }}>{resumen.porcentajeGeneral}%</div>
          </div>
        </div>

        {/* Tabla */}
        <div className="rg-tabla-wrap">
          <table className="rg-tabla">
            <thead>
              <tr>
                <th>Platillo</th>
                <th>Categoría</th>
                <th className="rg-num">Cant.</th>
                <th className="rg-num">Ventas</th>
                <th className="rg-num">Costo</th>
                <th className="rg-num">% Costo</th>
                <th className="rg-num">Utilidad</th>
                <th className="rg-num">% Utilidad</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={`${p.idPlatillo}-${i}`}>
                  <td>{p.nombre}</td>
                  <td>{p.categoria}</td>
                  <td className="rg-num">{formatNumber(p.cantidadVendida)}</td>
                  <td className="rg-num">{formatMoney(p.totalVentas)}</td>
                  <td className="rg-num">{formatMoney(p.totalCosto)}</td>
                  <td className="rg-num">
                    <span style={{
                      background: (p.totalVentas > 0 ? (p.totalCosto / p.totalVentas * 100) : 0) >= 50 ? '#dc262618' : '#d9770618',
                      color: (p.totalVentas > 0 ? (p.totalCosto / p.totalVentas * 100) : 0) >= 50 ? '#dc2626' : '#d97706',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {p.totalVentas > 0 ? ((p.totalCosto / p.totalVentas) * 100).toFixed(2) : '0.00'}%
                    </span>
                  </td>
                  <td className="rg-num" style={{ color: p.utilidad >= 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>{formatMoney(p.utilidad)}</td>
                  <td className="rg-num">
                    <span style={{
                      background: getUtilidadColor(p.porcentajeUtilidad) + '18',
                      color: getUtilidadColor(p.porcentajeUtilidad),
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {p.porcentajeUtilidad}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="rg-total-label">Total página:</td>
                <td></td>
                <td className="rg-num rg-total-val">{formatNumber(totalCantPag)}</td>
                <td className="rg-num rg-total-val">{formatMoney(totalVentasPag)}</td>
                <td className="rg-num rg-total-val">{formatMoney(totalCostoPag)}</td>
                <td className="rg-num rg-total-val">{totalVentasPag > 0 ? ((totalCostoPag / totalVentasPag) * 100).toFixed(2) + '%' : '0%'}</td>
                <td className="rg-num rg-total-val" style={{ color: '#16a34a' }}>{formatMoney(utilidadPag)}</td>
                <td className="rg-num rg-total-val">{totalVentasPag > 0 ? ((utilidadPag / totalVentasPag) * 100).toFixed(2) + '%' : '0%'}</td>
              </tr>
              {platillos.length > ITEMS_PER_PAGE && (
                <tr>
                  <td className="rg-total-label">Total general:</td>
                  <td></td>
                  <td className="rg-num rg-total-val">{formatNumber(platillos.reduce((s, p) => s + p.cantidadVendida, 0))}</td>
                  <td className="rg-num rg-total-val">{formatMoney(resumen.totalVentas)}</td>
                  <td className="rg-num rg-total-val">{formatMoney(resumen.totalCosto)}</td>
                  <td className="rg-num rg-total-val">{resumen.totalVentas > 0 ? ((resumen.totalCosto / resumen.totalVentas) * 100).toFixed(2) + '%' : '0%'}</td>
                  <td className="rg-num rg-total-val" style={{ color: '#16a34a' }}>{formatMoney(resumen.utilidadGeneral)}</td>
                  <td className="rg-num rg-total-val">{resumen.porcentajeGeneral}%</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderAjustesInventario = () => {
    const items = dataPaginada();
    if (items.length === 0) return <div className="rg-empty">No hay ajustes de inventario</div>;

    // Resumen
    const totalAjustes = ajustesInventario.length;
    const aumentos = ajustesInventario.filter(a => Number(a.diferencia) > 0);
    const disminuciones = ajustesInventario.filter(a => Number(a.diferencia) < 0);

    // Top ingredientes más ajustados
    const conteoIngredientes = {};
    ajustesInventario.forEach(a => {
      const nombre = a.ingrediente || 'Sin nombre';
      conteoIngredientes[nombre] = (conteoIngredientes[nombre] || 0) + 1;
    });
    const topAjustados = Object.entries(conteoIngredientes)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({
        name: name.length > 20 ? name.substring(0, 20) + '...' : name,
        ajustes: value
      }));

    return (
      <div>
        {/* KPI Cards */}
        <div className="rg-cards">
          <div className="rg-card rg-card--cuentas">
            <div className="rg-card__label">Total Ajustes</div>
            <div className="rg-card__valor">{totalAjustes}</div>
          </div>
          <div className="rg-card rg-card--neta">
            <div className="rg-card__label">Aumentos</div>
            <div className="rg-card__valor" style={{ color: '#16a34a' }}>{aumentos.length}</div>
          </div>
          <div className="rg-card" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="rg-card__label">Disminuciones</div>
            <div className="rg-card__valor" style={{ color: '#dc2626' }}>{disminuciones.length}</div>
          </div>
          <div className="rg-card rg-card--ticket">
            <div className="rg-card__label">Ingredientes Afectados</div>
            <div className="rg-card__valor">{Object.keys(conteoIngredientes).length}</div>
          </div>
        </div>

        {/* Chart Top ingredientes más ajustados */}
        {topAjustados.length > 0 && paginaActual === 1 && (
          <div className="rg-chart-full">
            <h4 className="rg-chart-title">Top 10 Ingredientes con más Ajustes</h4>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topAjustados} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip isMoney={false} />} />
                <Bar dataKey="ajustes" name="Ajustes" radius={[0, 4, 4, 0]}>
                  {topAjustados.map((_, i) => (
                    <Cell key={i} fill={COLORS_CHART[i % COLORS_CHART.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Tabla */}
        <div className="rg-tabla-wrap">
          <table className="rg-tabla">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Ingrediente</th>
                <th>Unidad</th>
                <th className="rg-num">Exist. Anterior</th>
                <th className="rg-num">Nueva Exist.</th>
                <th className="rg-num">Diferencia</th>
                <th>Comentarios</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a, i) => {
                const diff = Number(a.diferencia) || 0;
                return (
                  <tr key={i}>
                    <td>{a.fecha}</td>
                    <td>{a.hora}</td>
                    <td><strong>{a.ingrediente}</strong></td>
                    <td>{a.unidad}</td>
                    <td className="rg-num">{formatNumber(a.existenciaAnterior, 3)}</td>
                    <td className="rg-num">{formatNumber(a.nuevaExistencia, 3)}</td>
                    <td className="rg-num" style={{ color: diff > 0 ? '#16a34a' : diff < 0 ? '#dc2626' : '#666', fontWeight: '600' }}>
                      {diff > 0 ? '+' : ''}{formatNumber(diff, 3)}
                    </td>
                    <td>{a.usuario}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="rg-total-label" colSpan="6">Total registros página:</td>
                <td className="rg-num rg-total-val" colSpan="2">{items.length}</td>
              </tr>
              {ajustesInventario.length > ITEMS_PER_PAGE && (
                <tr>
                  <td className="rg-total-label" colSpan="6">Total registros general:</td>
                  <td className="rg-num rg-total-val" colSpan="2">{ajustesInventario.length}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    if (cargando) return <div className="rg-loading">Cargando datos...</div>;
    switch (tabActual) {
      case 'total-ventas': return renderTotalVentas();
      case 'ventas': return renderVentas();
      case 'detalle-platillos': return renderDetallePlatillos();
      case 'detalle-productos': return renderDetalleProductos();
      case 'cancelados': return renderCancelados();
      case 'meseros': return renderVentasPorMesero();
      case 'utilidad': return renderUtilidad();
      case 'ajustes-inventario': return renderAjustesInventario();
      default: return null;
    }
  };

  const showPagination = tabActual !== 'total-ventas' && getDataActual().length > ITEMS_PER_PAGE;

  return (
    <div className="rg-page">
      <Header titulo='Reportes Generales' color='#03A9F4' />

      <div className="rg-body">
        {/* ===== FILTROS COMPACTOS ===== */}
        <div className="rg-filtros">
          {/* Fila 1: Modo + Selector + Buscar */}
          <div className="rg-filtros__row1">
            <div className="rg-filtros__modo">
              <label className={`rg-radio ${modoFiltro === 'cortes' ? 'rg-radio--active' : ''}`}>
                <input type="radio" name="modo" checked={modoFiltro === 'cortes'} onChange={() => setModoFiltro('cortes')} />
                Por Cortes
              </label>
              <label className={`rg-radio ${modoFiltro === 'fechas' ? 'rg-radio--active' : ''}`}>
                <input type="radio" name="modo" checked={modoFiltro === 'fechas'} onChange={() => setModoFiltro('fechas')} />
                Por Fechas
              </label>
            </div>

            <div className="rg-filtros__selector">
              {modoFiltro === 'cortes' ? (
                <div className="rg-filtros__fechas">
                  <div className="rg-filtros__fecha-item">
                    <span>Fecha:</span>
                    <input type="date" value={fechaCorte} onChange={(e) => setFechaCorte(e.target.value)} />
                  </div>
                  {cortesDelDia.length > 1 ? (
                    <select value={corteSeleccionado} onChange={(e) => setCorteSeleccionado(e.target.value)}>
                      {cortesDelDia.map((c) => (
                        <option key={c.idCorte} value={c.idCorte}>
                          {c.status === 'actual' ? 'Actual (desde ultimo corte)' :
                           c.status === 'hoy' ? `Hoy (${c.fecha})` :
                           `Corte #${c.numeroCorte}: ${c.horaAnterior || ''} → ${c.hora}`}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="rg-corte-info">
                      {cortesDelDia.length === 0 ? 'No hay corte para esta fecha' :
                       cortesDelDia[0].status === 'actual' ? 'Actual (desde ultimo corte)' :
                       cortesDelDia[0].status === 'hoy' ? `Hoy (${cortesDelDia[0].fecha})` :
                       `${cortesDelDia[0].fechaAnterior || ''} ${cortesDelDia[0].horaAnterior || ''} → ${cortesDelDia[0].fecha} ${cortesDelDia[0].hora}`}
                    </span>
                  )}
                </div>
              ) : (
                <div className="rg-filtros__fechas">
                  <div className="rg-filtros__fecha-item">
                    <span>Inicio:</span>
                    <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
                  </div>
                  <div className="rg-filtros__fecha-item">
                    <span>Fin:</span>
                    <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
                  </div>
                </div>
              )}
            </div>

            <button onClick={buscarReportes} className="rg-btn-buscar" disabled={cargando}>
              <FaSearch />
              {cargando ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {/* Fila 2: Checkboxes inline */}
          <div className="rg-filtros__row2">
            <div className="rg-checks">
              <span className="rg-checks__title">Tipo Cuenta:</span>
              {Object.entries(tiposCuenta).map(([key, val]) => (
                <label className="rg-check" key={key}>
                  <input type="checkbox" checked={val} onChange={() => handleTipoCuentaChange(key)} />
                  {key.toUpperCase()}
                </label>
              ))}
            </div>
            <div className="rg-checks__sep"></div>
            <div className="rg-checks">
              <span className="rg-checks__title">Tipo Pago:</span>
              {Object.entries(tiposPago).map(([key, val]) => (
                <label className="rg-check" key={key}>
                  <input type="checkbox" checked={val} onChange={() => handleTipoPagoChange(key)} />
                  {key.toUpperCase()}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ===== PANEL RESULTADOS ===== */}
        <div className="rg-resultados">
          {/* Tabs + acciones */}
          <div className="rg-toolbar">
            <div className="rg-tabs">
              {[
                { id: 'total-ventas', label: 'Total Ventas' },
                { id: 'ventas', label: `Ventas (${pedidosReporte.length})` },
                { id: 'detalle-platillos', label: `Platillos (${detallePlatillos.length})` },
                { id: 'detalle-productos', label: `Productos (${detalleProductos.length})` },
              { id: 'cancelados', label: `Cancelados (${productosCancelados.length})` },
                { id: 'meseros', label: `Meseros (${ventasPorMesero.length})` },
                { id: 'utilidad', label: `Utilidad (${reporteUtilidad.platillos.length})` },
                { id: 'ajustes-inventario', label: `Ajustes Inv. (${ajustesInventario.length})` }
              ].map(tab => (
                <button
                  key={tab.id}
                  className={`rg-tab ${tabActual === tab.id ? 'rg-tab--active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="rg-toolbar__actions">
              {showPagination && (
                <div className="rg-pag">
                  <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1}>⟪</button>
                  <button onClick={() => setPaginaActual(p => Math.max(1, p - 1))} disabled={paginaActual === 1}>⟨</button>
                  <span className="rg-pag__info">{paginaActual}/{totalPaginas}</span>
                  <button onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))} disabled={paginaActual === totalPaginas}>⟩</button>
                  <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual === totalPaginas}>⟫</button>
                  <span className="rg-pag__total">{getDataActual().length} reg.</span>
                </div>
              )}
              <button onClick={exportarCSV} className="rg-btn-csv">
                <FaFileExcel /> CSV
              </button>
            </div>
          </div>

          {/* Contenido del tab */}
          <div className="rg-content">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportesGenerales;
